/*!

=========================================================
* Black Dashboard React v1.2.2
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/black-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
/*eslint-disable*/
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
// nodejs library to set properties for components
import { PropTypes } from "prop-types";

// reactstrap components
import { Nav } from "reactstrap";
import { BackgroundColorContext } from "contexts/BackgroundColorContext";

// Platform Switch
import PlatformSwitch from "components/PlatformSwitch/PlatformSwitch";
import { usePlatform } from "contexts/PlatformContext";
import "./Sidebar.css";
import { canAccessRoute } from "utils/permissions";
import { SIDEBAR_GROUP_ORDER } from "routes";

function Sidebar(props) {
  const location = useLocation();
  const sidebarRef = React.useRef(null);
  const { currentPlatform, getPlatformInfo } = usePlatform();

  const linkOnClick = () => {
    document.documentElement.classList.remove("nav-open");
  };

  const { routes, rtlActive } = props;

  // 過濾路由：顯示當前平台的路由 + 跨平台路由 + 系統管理
  const filteredRoutes = routes.filter((route) => {
    if (route.invisible) return false; // 明確隱藏的路由不出現在 sidebar
    if (route.redirect) return false;
    if (!route.platform) return true; // 沒有標記平台的路由永遠顯示
    return route.platform === currentPlatform
      || route.platform === "common"
      || route.platform === "system";
  });

  // 權限：畫不畫由 canAccessRoute 決定（permission AND requiredCap）。
  // user 還沒載入時一律不畫，不要閃一下再消失。
  const visible = (list) =>
    props.user ? list.filter((r) => canAccessRoute(props.user, r)) : [];

  // "system"（v2.32.0）：帳號 / 稽核這類**後臺自身的管理**，不是業務數據。
  // 刻意跟 common 分開 —— 把使用者管理混進業務導覽語意不對。
  const commonRoutes = visible(filteredRoutes.filter((r) => r.platform === "common"));
  const platformRoutes = visible(filteredRoutes.filter((r) => r.platform === currentPlatform));
  const systemRoutes = visible(filteredRoutes.filter((r) => r.platform === "system"));

  // 平台清單分組：先照 SIDEBAR_GROUP_ORDER，routes.js 沒列到的組接在後面，
  // 沒有 group 的路由放最前面（OA 目前全部沒有 group → 就是一段平面清單）。
  const groupedPlatform = (() => {
    const byGroup = new Map();
    platformRoutes.forEach((r) => {
      const key = r.group || "";
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push(r);
    });
    const order = ["", ...SIDEBAR_GROUP_ORDER, ...[...byGroup.keys()].filter(
      (k) => k !== "" && !SIDEBAR_GROUP_ORDER.includes(k)
    )];
    return order
      .filter((k) => byGroup.has(k))
      .map((k) => ({ title: k, routes: byGroup.get(k) }));
  })();

  const platformInfo = getPlatformInfo();

  // variant: "platform"（預設）/ "common"（營收數據）/ "system"（底部系統區）
  const renderLink = (prop, key, variant = "platform") => {
    const isActive = location.pathname === `/admin${prop.path}`;
    return (
      <li
        className={[
          "sidebar-nav-item",
          `sidebar-nav-item--${variant}`,
          isActive ? "active is-current" : "",
        ].join(" ").trim()}
        key={key}
      >
        <NavLink
          to={prop.layout + prop.path}
          className="nav-link sidebar-nav-link"
          onClick={() => { linkOnClick(); if (props.toggleSidebar) props.toggleSidebar(); }}
          aria-current={isActive ? "page" : undefined}
        >
          <i className={prop.icon} aria-hidden="true" />
          <p>{rtlActive ? prop.rtlName : prop.name}</p>
        </NavLink>
      </li>
    );
  };

  const renderCaption = (title, key) => (
    <li className="sidebar-caption" key={key}>
      <span className="sidebar-caption-text">{title}</span>
    </li>
  );

  return (
    <BackgroundColorContext.Consumer>
      {({ color }) => (
        <div className="sidebar sidebar-modern" data={color}>
          <div className="sidebar-wrapper" ref={sidebarRef}>
            <PlatformSwitch />

            <Nav>
              {/* 跨平台（營收數據）：排在平台標題上面，切換平台時不動 */}
              {commonRoutes.map((prop, key) => renderLink(prop, `common-${key}`, "common"))}

              {/* 當前平台 */}
              {platformInfo && platformRoutes.length > 0 && (
                <>
                  {/* 只留分隔線，不再寫一次平台名 —— 上面的切換器已經說了現在在哪個平台 */}
                  <li className="sidebar-divider" key="divider-platform" />
                  {groupedPlatform.map((g) => (
                    <React.Fragment key={`group-${g.title || "flat"}`}>
                      {g.title && (
                        <li className="sidebar-group-caption">
                          <span>{g.title}</span>
                        </li>
                      )}
                      {g.routes.map((prop, key) => renderLink(prop, `${g.title}-${key}`))}
                    </React.Fragment>
                  ))}
                </>
              )}

              {/* 系統管理（v2.32.0）—— 沉在最下面。整區只有 manage_users 的人看得到；
                  沒有任何一條可見時連標題都不畫，不要在別人的側欄留一塊空的。 */}
              {systemRoutes.length > 0 && (
                <>
                  <li className="sidebar-system-spacer" key="system-spacer" />
                  {renderCaption("系統", "caption-system")}
                  {systemRoutes.map((prop, key) => renderLink(prop, `system-${key}`, "system"))}
                </>
              )}
            </Nav>
          </div>
        </div>
      )}
    </BackgroundColorContext.Consumer>
  );
}

Sidebar.propTypes = {
  // if true, then instead of the routes[i].name, routes[i].rtlName will be rendered
  // insde the links of this component
  rtlActive: PropTypes.bool,
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    // innerLink is for links that will direct the user within the app
    // it will be rendered as <Link to="...">...</Link> tag
    innerLink: PropTypes.string,
    // outterLink is for links that will direct the user outside the app
    // it will be rendered as simple <a href="...">...</a> tag
    outterLink: PropTypes.string,
    // the text of the logo
    text: PropTypes.node,
    // the image src of the logo
    imgSrc: PropTypes.string,
  }),
};

export default Sidebar;
